/**
 * Notification Models and Interfaces
 * Enums match backend C# enums (AvsecPortal.Domain.Enums)
 */

// Enums (matching backend)
export enum ResourceType {
  File = 1,
  Service = 2
}

export enum NotificationAction {
  Granted = 1,
  Revoked = 2,
  Updated = 3,
  Restricted = 4,
  Expired = 5
}

export enum PermissionType {
  Read = 1,
  Write = 2,
  Delete = 3
}

export enum NotificationType {
  FilePermission = 1,
  ServicePermission = 2
}

/**
 * Generic Notification Event Interface
 * Matches backend NotificationEventDto
 */
export interface NotificationEvent {
  id: number;
  userId: number;                          // Database user ID
  iamUserId: number;                       // IAM user ID
  type: NotificationType;                  // Numeric enum: 1 or 2
  resourceName: string;
  resourceType: ResourceType;              // Numeric enum: 1 or 2
  action?: NotificationAction;             // Numeric enum: 1-5
  permissionType?: PermissionType | null;  // Numeric enum: 1-3
  metadata?: string | null;                // File ID or Service ID (as JSON string or number)
  isRead: boolean;
  readAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

/**
 * File Permission Notification (extends NotificationEvent)
 */
export interface FilePermissionNotification extends NotificationEvent {
  type: NotificationType.FilePermission;
  resourceType: ResourceType.File;
  permissionType: PermissionType;
  action: NotificationAction;
}

/**
 * Service Permission Notification (extends NotificationEvent)
 */
export interface ServicePermissionNotification extends NotificationEvent {
  type: NotificationType.ServicePermission;
  resourceType: ResourceType.Service;
  serviceStatus: string;
  action: NotificationAction;
}

/**
 * Paginated Notifications Response
 */
export interface NotificationsResponse {
  success: boolean;
  message: string;
  data: {
    items: NotificationEvent[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  };
  errors: string[] | null;
}

/**
 * Single Notification Response (for mark as read)
 */
export interface NotificationSingleResponse {
  success: boolean;
  message: string;
  data: {
    id: number;
    isRead: boolean;
  };
  errors: string[] | null;
}

/**
 * Mark All as Read Response
 */
export interface MarkAllAsReadResponse {
  success: boolean;
  message: string;
  data: {
    updatedCount: number;
  };
  errors: string[] | null;
}

/**
 * Delete Notification Response
 */
export interface DeleteNotificationResponse {
  success: boolean;
  message: string;
  data: null;
  errors: string[] | null;
}

/**
 * Unread Count Response
 */
export interface UnreadCountResponse {
  success: boolean;
  message: string;
  data: {
    unreadCount: number;
  };
  errors: string[] | null;
}

/**
 * Utility functions for enum conversions
 * Backend sends enum values as strings, so we need to convert them
 */
export class NotificationEnumConverter {
  /**
   * Convert string enum value to NotificationType enum
   */
  static toNotificationType(value: string): NotificationType {
    switch (value?.toLowerCase()) {
      case 'filepermission':
        return NotificationType.FilePermission;
      case 'servicepermission':
        return NotificationType.ServicePermission;
      default:
        return NotificationType.FilePermission;
    }
  }

  /**
   * Convert string enum value to ResourceType enum
   */
  static toResourceType(value: string | number): ResourceType {
    if (typeof value === 'number') {
      return value;
    }
    switch (value?.toLowerCase()) {
      case 'file':
      case '1':
        return ResourceType.File;
      case 'service':
      case '2':
        return ResourceType.Service;
      default:
        return ResourceType.File;
    }
  }

  /**
   * Convert string enum value to NotificationAction enum
   */
  static toNotificationAction(value: string | number): NotificationAction {
    if (typeof value === 'number') {
      return value;
    }
    switch (value?.toLowerCase()) {
      case 'granted':
      case '1':
        return NotificationAction.Granted;
      case 'revoked':
      case '2':
        return NotificationAction.Revoked;
      case 'updated':
      case '3':
        return NotificationAction.Updated;
      case 'restricted':
      case '4':
        return NotificationAction.Restricted;
      case 'expired':
      case '5':
        return NotificationAction.Expired;
      default:
        return NotificationAction.Granted;
    }
  }

  /**
   * Convert string enum value to PermissionType enum
   */
  static toPermissionType(value: string | number | null): PermissionType | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    switch (value?.toLowerCase()) {
      case 'read':
      case '1':
        return PermissionType.Read;
      case 'write':
      case '2':
        return PermissionType.Write;
      case 'delete':
      case '3':
        return PermissionType.Delete;
      default:
        return null;
    }
  }
}
